import React, { useMemo } from 'react'
import { Cell, colors, Divider, H2, H5, IconButton, LayoutBand, List, Row, Separator, Subheading } from '@fs/zion-ui'
import { NoticeInfo } from '@fs/zion-icon'
import { useTranslation } from 'react-i18next'
import { useR2StateContext } from '../../../../providers/r2Provider/R2Context'
import PersonDisplay from './PersonDisplay'
import { sortPersonsByPrimary } from '../utils/display.utils'
import useGroupSectionsRework from '../hooks/useGroupSectionsRework'

/**
 * FloatingPersonsList
 *
 * Renders persons that are not attached to any group ("floating" persons) as a list, under a
 * "Names not in a group" heading with an info control explaining how to move them.
 *
 * Before the group sections rework the heading is a grey band pointing at Edit Households, a
 * destination this release removes.
 *
 * @component
 * @param {Object} props
 * @param {Array} props.persons - Array of floating person objects to display
 * @param {Function} [props.onPersonSelect] - Callback when a person in the list is selected
 * @returns {JSX.Element|null} The floating persons list, or null when there are no persons
 */
const FloatingPersonsList = ({ persons = [], onPersonSelect }) => {
  const [t] = useTranslation()
  const groupSectionsRework = useGroupSectionsRework()
  const { highlightedPersonId } = useR2StateContext()

  const sortedPersons = useMemo(() => sortPersonsByPrimary(persons), [persons])

  if (sortedPersons.length === 0) return null

  const heading = t('household-list.names-not-in-group', 'Names not in a group')

  const rows = (
    <List>
      {sortedPersons.map((person) => (
        <PersonDisplay
          key={person?.id}
          person={person}
          isSelected={highlightedPersonId === person?.id}
          onPersonSelect={onPersonSelect}
        />
      ))}
    </List>
  )

  if (groupSectionsRework) {
    return (
      <section aria-label={heading} data-testid="names-not-in-a-group">
        <Row alignY="middle" guttersY="pico">
          <H2 size="H6" growX>
            {heading}
          </H2>
          <IconButton
            Icon={NoticeInfo}
            size="xs"
            aria-label={t('household-list.names-not-in-group.info-label', 'About names not in a group')}
            description={t(
              'household-list.names-not-in-group.info-description',
              'To add to an existing group, open a person in that group and edit Relationships. To start a new group, use New group.'
            )}
            data-testid="names-not-in-a-group-info"
          />
        </Row>
        <Separator size="nano" />
        {rows}
        <Divider marginY="xxs" fullWidth />
      </section>
    )
  }

  return (
    <>
      <LayoutBand color={colors.gray02} marginY="xs">
        <Row>
          <Cell growX alignY="middle">
            <H5>
              {t('household-list.names-not-in-household', 'Names not in a household')}
              <Subheading size="xs" secondary>
                {t(
                  'household-list.names-not-in-household-description',
                  'Open a name to review it, or rearrange names in Edit Households.'
                )}
              </Subheading>
            </H5>
          </Cell>
        </Row>
      </LayoutBand>
      {rows}
    </>
  )
}

export default FloatingPersonsList
